import React from "react";

import { CheckCircleFilled, CheckOutlined } from "@ant-design/icons";

import PanelCard from "./PanelCard";

import { ReactComponent as FlowersIllustration } from "../../../../../assets/images/subscriptions/Flowers.svg";

import "./TermSummary.css";

const DEFAULT_FEATURES = [
  { key: "standard-rates", text: "Billed at standard plan rates." },
  {
    key: "cross-location",
    text: "Test credits can be used across all locations and categories.",
  },
];

const getBillingPeriodSuffix = (billingCycle = "") => {
  const normalized = String(billingCycle).toLowerCase();
  if (/semi|half|bi-?annual/.test(normalized)) {
    return "half-year";
  }
  if (normalized.includes("annual") || normalized.includes("year")) {
    return "year";
  }
  if (normalized.includes("quarter")) {
    return "quarter";
  }
  if (/bi-?weekly|fortnight/.test(normalized)) {
    return "2 weeks";
  }
  if (normalized.includes("week")) {
    return "week";
  }
  if (normalized.includes("month")) {
    return "month";
  }
  return "cycle";
};

const getBillingCycleText = (billingCycle) => (
  String(billingCycle || "").trim().toLowerCase() || "monthly"
);

export default function TermSummary({
  title = "Term Summary",
  summaryCards = {},
  statusLabel = "On Track",
  termLengthLabel = "Total Term Length",
  billingCycleLabel = "Billing Cycle",
  totalTermTestsLabel = "Total Term Tests",
  totalTermTests,
  aboutTitle = "About your subscription",
  aboutDescription,
  features = DEFAULT_FEATURES,
}) {
  const {
    termLabel,
    termLength,
    termDateRange,
    billingCycle,
    monthlyBillingAmount,
    testsTotal,
    subscriptionMode,
  } = summaryCards;
  const isRampup = subscriptionMode === "rampup";
  const termBadgeLabel = isRampup ? "Currently in Rampup" : termLabel;

  const resolvedTotalTermTests = totalTermTests ?? testsTotal;
  const billingPeriodSuffix = getBillingPeriodSuffix(billingCycle);
  const billingDetailPrefix = billingCycle ? `${billingCycle} billing:` : null;
  const resolvedAboutDescription = aboutDescription
    ?? `This is a term-based subscription with ${getBillingCycleText(billingCycle)} billing.`;

  return (
    <PanelCard className="TermSummary" as="article">
      <FlowersIllustration
        className="TermSummary__Flower TermSummary__Flower--topLeft"
        aria-hidden
      />
      <FlowersIllustration
        className="TermSummary__Flower TermSummary__Flower--topRight"
        aria-hidden
      />
      <FlowersIllustration
        className="TermSummary__Flower TermSummary__Flower--bottomRight"
        aria-hidden
      />

      <div className="TermSummary__Content">
        <header className="TermSummary__Header">
          <h2 className="TermSummary__Title">{title}</h2>
          <div className="TermSummary__Badges">
            {termBadgeLabel && (
              <span className="TermSummary__Badge TermSummary__Badge--term">
                {termBadgeLabel}
              </span>
            )}
            {statusLabel && (
              <span className="TermSummary__Badge TermSummary__Badge--status">
                <CheckCircleFilled aria-hidden />
                {statusLabel}
              </span>
            )}
          </div>
        </header>

        <div className="TermSummary__Body">
          <div className="TermSummary__MetricsPanel">
            <div className="TermSummary__Metrics">
              <div className="TermSummary__Metric">
                <div className="TermSummary__MetricMain">
                  <p className="TermSummary__MetricLabel">{termLengthLabel}</p>
                  {termLength != null && termLength !== "" && (
                    <p className="TermSummary__MetricValue">{termLength}</p>
                  )}
                </div>
                {termDateRange != null && termDateRange !== "" && (
                  <p className="TermSummary__MetricDetail TermSummary__MetricDetail--emphasis">
                    {termDateRange}
                  </p>
                )}
              </div>

              <div className="TermSummary__MetricDivider" aria-hidden />

              <div className="TermSummary__Metric">
                <div className="TermSummary__MetricMain">
                  <p className="TermSummary__MetricLabel">{billingCycleLabel}</p>
                  {billingCycle != null && billingCycle !== "" && (
                    <p className="TermSummary__MetricValue">{billingCycle}</p>
                  )}
                </div>
                {monthlyBillingAmount != null && billingDetailPrefix && (
                  <p className="TermSummary__MetricDetail">
                    <span className="TermSummary__MetricDetailLabel">
                      {`${billingDetailPrefix} `}
                    </span>
                    <span className="TermSummary__MetricDetailValue">
                      {`$${Number(monthlyBillingAmount).toLocaleString()}`}
                    </span>
                    <span className="TermSummary__MetricDetailSuffix">
                      {` / ${billingPeriodSuffix}`}
                    </span>
                  </p>
                )}
              </div>

              <div className="TermSummary__MetricDivider" aria-hidden />

              <div className="TermSummary__Metric">
                <div className="TermSummary__MetricMain">
                  <p className="TermSummary__MetricLabel">{totalTermTestsLabel}</p>
                  {resolvedTotalTermTests != null && (
                    <p className="TermSummary__MetricValue">
                      {Number(resolvedTotalTermTests).toLocaleString()}
                    </p>
                  )}
                </div>
                <p className="TermSummary__MetricDetail">
                  All test credits given at once
                </p>
              </div>
            </div>
          </div>

          <div className="TermSummary__About">
            <div className="TermSummary__AboutIntro">
              <h3 className="TermSummary__AboutTitle">{aboutTitle}</h3>
              <p className="TermSummary__AboutDescription">{resolvedAboutDescription}</p>
            </div>
            <ul className="TermSummary__AboutList">
              {features.map((feature) => (
                <li className="TermSummary__AboutItem" key={feature.key}>
                  <CheckOutlined aria-hidden />
                  <span>{feature.text}</span>
                </li>
              ))}
              <li className="TermSummary__AboutItem">
                <CheckOutlined aria-hidden />
                <span>
                  Need more credits? Top-up credits or Contact your account manager to adjust your plan.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </PanelCard>
  );
}
